const DIRECT_SOURCE_KEYS = new Set([
  "ebay",
  "digikey",
  "newark",
  "netilion",
  "schneider",
  "siemens",
  "arrow",
  "tme",
  "nexar",
]);

export function normalizeOutboundSourceKey(sourceId: unknown): string | null {
  const normalized = typeof sourceId === "string" ? sourceId.trim().toLowerCase() : "";
  return /^[a-z0-9_-]{1,80}$/.test(normalized) ? normalized : null;
}

export function outboundSourceKey(sourceId: string | null | undefined): string {
  return normalizeOutboundSourceKey(sourceId) ?? "web";
}

/** Presentation category for the fixed UI palette and source semantics. */
export function supplierDisplaySourceKey(sourceId: string | null | undefined): string {
  const normalized = normalizeOutboundSourceKey(sourceId);
  return normalized && DIRECT_SOURCE_KEYS.has(normalized) ? normalized : "web";
}

export function compactOfferReference(value: string): string {
  if (/^[a-z0-9_:-]{1,160}$/i.test(value)) return value;
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    first = Math.imul(first ^ code, 0x01000193) >>> 0;
    second = Math.imul(second ^ code, 0x85ebca6b) >>> 0;
  }
  return `offer-${first.toString(16).padStart(8, "0")}${second.toString(16).padStart(8, "0")}`;
}

export function outboundCapabilityIsFresh(
  expiresAt: string | null | undefined,
  now = Date.now(),
  minimumValidityMs = 5_000,
): boolean {
  const expiry = new Date(expiresAt || "").getTime();
  return Number.isFinite(expiry) && expiry > now + minimumValidityMs;
}

export function buildSupplierRedirectPath(input: {
  destination: string;
  /** Short-lived server capability binding this exact supplier destination. */
  outboundCapability?: string | null;
  searchId?: string | null;
  /** One-search ownership proof; never use the persistent anonymous id here. */
  searchProof?: string | null;
  sourceKey?: string | null;
  offerId?: string | null;
  surface?: string;
  partSlug?: string | null;
  normalizedMpn?: string | null;
  rankPosition?: number | null;
  resultCount?: number | null;
  matchType?: "exact" | "variant" | "fuzzy" | null;
  scanConfidence?: "high" | "medium" | "review" | null;
  costIsComplete?: boolean | null;
}): string {
  const params = new URLSearchParams({ url: input.destination });
  if (input.outboundCapability) params.set("cap", input.outboundCapability);
  if (input.searchId) params.set("search", input.searchId);
  if (input.searchId && input.searchProof) params.set("proof", input.searchProof);
  if (input.sourceKey) params.set("source", input.sourceKey);
  if (input.offerId) params.set("offer", compactOfferReference(input.offerId));
  if (input.surface) params.set("surface", input.surface);
  if (input.partSlug) params.set("part", input.partSlug);
  if (input.normalizedMpn) params.set("mpn", input.normalizedMpn);
  if (Number.isInteger(input.rankPosition) && Number(input.rankPosition) > 0) {
    params.set("rank", String(input.rankPosition));
  }
  if (Number.isInteger(input.resultCount) && Number(input.resultCount) > 0) {
    params.set("count", String(input.resultCount));
  }
  if (input.matchType) params.set("match", input.matchType);
  if (input.scanConfidence) params.set("confidence", input.scanConfidence);
  if (input.costIsComplete != null) params.set("cost_complete", input.costIsComplete ? "1" : "0");
  return `/api/sourcing/outbound?${params.toString()}`;
}
